<template>
  <div class="picture-page">
    <div v-if="loading" class="loading">
      <van-loading type="spinner" />
    </div>
    <div v-else>
      <van-empty v-if="imageGroups.length === 0" description="No pictures" />
      <div v-else>
        <div v-for="group in imageGroups" :key="group.docType" class="image-group">
          <div class="title">{{ group.title }}</div>
          <div class="divider"></div>
          <div>
            <img
              v-for="item in group.items"
              :key="item.id"
              :src="item.src"
              class="image"
              @click="openViewer(item)"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-if="imageGroups.length > 0" class="wm-overlay-in-page" :style="wmStyle"></div>
    <ImageViewer v-model:show="viewerShow" :src="viewerSrc" :watermark-text="userName" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { showToast } from 'vant'
import { getAttachments, getPresignedUrl } from '@/api/attachment'
import { docTypeList } from '@/utils/attachment-config'
import ImageViewer from '@/components/ImageViewer.vue'
import { getCurrentUser } from '@/api/login'

const props = defineProps<{ detailData: any }>()

const route = useRoute()
const batchId = computed(() => String(route.query.batchId || ''))

const loading = ref(false)
const imageGroups = ref<{ docType: string; title: string; items: { id: any; src: string }[] }[]>([])
const viewerShow = ref(false)
const viewerSrc = ref('')
const userName = ref('')
const wmStyle = computed(() => {
  const text = userName.value || ''
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='260' height='260'><text x='30%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='rgba(0,0,0,0.12)' font-size='12' font-family='system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif' transform='rotate(-30,130,130)'>${text}</text></svg>`
  const url = 'data:image/svg+xml;utf8,' + encodeURIComponent(svg)
  return { backgroundImage: `url("${url}")` }
})

const isImageType = (fileType: string) => {
  const t = (fileType || '').toLowerCase()
  return ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'].some((ext) => t.includes(ext))
}

const fetchImages = async () => {
  if (!batchId.value) return
  loading.value = true
  try {
    const res = await getAttachments(batchId.value)
    const list = Array.isArray(res.data) ? res.data : []
    const imgs = list.filter((it: any) => isImageType(String(it.fileType || '')))
    const flat: { id: any; docType: string; src: string }[] = new Array(imgs.length)
    await Promise.all(
      imgs.map(async (it: any, idx: number) => {
        try {
          if (it.url) {
            const r = await getPresignedUrl(it.url, false)
            flat[idx] = { id: it.id, docType: String(it.docType || ''), src: r.data }
          } else {
            flat[idx] = { id: it.id, docType: String(it.docType || ''), src: '' }
          }
        } catch (e) {}
      }),
    )
    const valid = flat.filter(Boolean)
    const map = new Map<
      string,
      { docType: string; title: string; items: { id: any; src: string }[] }
    >()
    for (const item of valid) {
      const key = item.docType
      if (!key) continue
      let group = map.get(key)
      if (!group) {
        const conf = docTypeList.find((d) => d.name === key)
        group = { docType: key, title: conf ? conf.value : key, items: [] }
        map.set(key, group)
      }
      group.items.push({ id: item.id, src: item.src })
    }
    const orderIndex = (k: string) => {
      const i = docTypeList.findIndex((d) => d.name === k)
      return i === -1 ? Number.MAX_SAFE_INTEGER : i
    }
    imageGroups.value = Array.from(map.values()).sort(
      (a, b) => orderIndex(a.docType) - orderIndex(b.docType),
    )
  } catch (error) {
    showToast('Failed to load pictures')
  } finally {
    loading.value = false
  }
}

onMounted(fetchImages)

onMounted(async () => {
  const cached = sessionStorage.getItem('user_name') || ''
  if (cached) {
    userName.value = cached
  } else {
    try {
      const result = await getCurrentUser()
      userName.value = String((result as any)?.data?.user?.userName || '')
      if (userName.value) sessionStorage.setItem('user_name', userName.value)
    } catch {}
  }
})

const openViewer = (item: { src: string }) => {
  viewerSrc.value = item.src
  viewerShow.value = true
}
</script>

<style scoped lang="scss">
.picture-page {
  padding: 0 10px;
  position: relative;
}

@media (orientation: landscape) {
  .picture-page {
    zoom: 0.5;
  }
}

.loading {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}
.image-group {
  margin-bottom: 16px;
}
.title {
  font-size: 14px;
  color: #323233;
  border: none;
}
.divider {
  border-bottom: 1px solid #ebedf0;
  margin: 6px 0 10px 0;
}
.image {
  width: 100%;
  height: auto;
  display: block;
  margin-bottom: 10px;
}
.wm-overlay-in-page {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
  background-repeat: repeat;
  background-size: 260px 260px;
}
</style>
